import React from "react";
import {
  EnvelopeSimple,
  IdentificationCard,
  Phone,
  Storefront,
  User,
} from "@phosphor-icons/react";

import { Icon } from "../../../design-system/components/Icon";
import { Avatar as DskAvatar } from "../../../design-system/components/desktop";
import { Avatar as DsAvatar } from "../../../design-system/components/mobile";
import { useViewport } from "../../../design-system/hooks/useViewport";
import {
  Panel,
  PanelAction,
  PanelBadge,
} from "../../../design-system/patterns/settings";

/**
 * Basic information, the first panel on /profile. Pure.
 *
 * The verified chip is a STATE, so it is a pill and never a button. The role
 * chip beside it is the same. The single trailing action is the edit command,
 * ghost on both tracks, which is the panel's one-action rule.
 */
const BasicInfo = ({ profile, onEdit }) => {
  const { isDesktop } = useViewport();
  const name = profile?.full_name || "Your profile";
  const store = profile?.store_name || profile?.collection_name;

  // Both tracks ship an avatar and they are not the same element: the desktop
  // one is a 32px grey-gradient disc that needs enlarging, the mobile one has
  // a real `--lg` at exactly the 72px this block wants. So the track picks it,
  // and `pfx-avatar` only has to do the resizing job on the desktop half.
  const avatar = profile?.profile_image ? (
    <img src={profile.profile_image} alt="" />
  ) : (
    <Icon as={User} size="xl" />
  );

  return (
    <Panel
      icon={IdentificationCard}
      title="Basic information"
      action={<PanelAction onClick={onEdit}>Edit profile</PanelAction>}
    >
      <div className="pfx-identity">
        {isDesktop ? (
          <DskAvatar className="pfx-avatar" name={profile?.full_name || undefined}>
            {avatar}
          </DskAvatar>
        ) : (
          <DsAvatar size="lg" fill="neutral" className="pfx-avatar--mobile">
            {avatar}
          </DsAvatar>
        )}

        <div className="pfx-identity__text">
          <div className="pfx-identity__name">
            <h3>{name}</h3>
            {profile?.verified ? (
              <PanelBadge tone="success">Verified</PanelBadge>
            ) : null}
            {profile?.role ? <PanelBadge>{profile.role}</PanelBadge> : null}
          </div>

          <ul className="pfx-identity__facts">
            <li>
              <Icon as={EnvelopeSimple} size="sm" />
              <span>{profile?.email || "No email on file"}</span>
            </li>
            <li>
              <Icon as={Phone} size="sm" />
              <span>{profile?.phone_number || "No phone number on file"}</span>
            </li>
            <li>
              <Icon as={Storefront} size="sm" />
              <span>{store || "No store name yet"}</span>
            </li>
          </ul>
        </div>
      </div>
    </Panel>
  );
};

export default BasicInfo;
